import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { teilnehmerlisteAPI } from '@/lib/api';

/* Teilnehmerliste in der App („Wer kommt noch?“), Betreiberentscheidung 03.10.2026:
   Mitglieder sehen die Vornamen der angemeldeten Mitglieder — ohne Einwilligungsabfrage,
   dafür mit Hinweis in der App und Widerspruch im Profil. Zwei Schalter, beide Standard AUS.
   - Streng gelesen: nur exakt true gilt als „an“. Ein fehlender oder kaputter Wert darf
     hier nie wie „eingeschaltet“ aussehen.
   - Einschalten braucht einen zweiten, bewussten Klick (kein window.confirm); Ausschalten
     wirkt sofort.
   - Schalten darf nur der Inhaber (Rolle admin). Der Server prüft das selbst, die Karte
     sperrt die Schalter nur, damit niemand erst beim Speichern davon erfährt. */

const istStand = (d) => !!d && typeof d === 'object' && !Array.isArray(d);
const zahl = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : '–');

// Der Server speichert UTC. Fehlt die Zonenangabe, ist UTC gemeint — sonst läse der
// Browser den Wert als Ortszeit und die Uhrzeit stünde zwei Stunden daneben.
function zeitpunkt(iso) {
  if (!iso) return '';
  let s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) && !/(Z|[+-]\d{2}:?\d{2})$/.test(s)) s += 'Z';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function Wert({ children }) {
  return <span className="font-medium text-foreground">{children}</span>;
}

/* Eine Schalterzeile. Beim Einschalten ersetzt der Bestätigungsblock den Schalter:
   „Abbrechen“ steht oben rechts, genau dort, wo eben noch der Schalter war — ein
   Doppelklick bricht ab, statt einzuschalten. Deshalb ist die Zeile oben ausgerichtet
   (items-start): die Stelle bleibt dieselbe, auch wenn der Text links neu umbricht. */
function SchalterZeile({ testid, titel, text, an, gesperrt, sperrgrund, wartet, frage, onSchalten, onJa, onAbbrechen }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">{titel}</p>
        <p className="text-xs text-muted-foreground">{text}</p>
        {wartet && (
          <p className="text-xs text-amber-700 dark:text-amber-400 mt-1.5" data-testid={`${testid}-frage`}>{frage}</p>
        )}
      </div>
      {wartet ? (
        // Schmal untereinander, breit nebeneinander — „Abbrechen“ in beiden Fällen oben rechts.
        <div className="flex flex-col-reverse sm:flex-row items-end sm:items-center gap-2 shrink-0" data-testid={`${testid}-bestaetigen`}>
          <Button size="sm" onClick={onJa} data-testid={`${testid}-ja`}>Ja, einschalten</Button>
          <Button size="sm" variant="outline" onClick={onAbbrechen} autoFocus data-testid={`${testid}-abbrechen`}>Abbrechen</Button>
        </div>
      ) : (
        <Switch
          className="mt-0.5"
          checked={an}
          onCheckedChange={onSchalten}
          disabled={gesperrt}
          aria-label={titel}
          title={gesperrt && sperrgrund ? sperrgrund : undefined}
          data-testid={`${testid}-toggle`}
        />
      )}
    </div>
  );
}

export default function TeilnehmerlisteKarte() {
  const { user } = useAuth();
  const istInhaber = user?.role === 'admin';
  // null = lädt · 'verboten' = kein Recht (Karte bleibt weg) · 'fehler' = Laden gescheitert · sonst Antwort des Servers
  const [stand, setStand] = useState(null);
  const [busy, setBusy] = useState(false);
  // Schlüssel des Schalters, dessen Einschalten gerade bestätigt werden soll.
  const [bestaetigen, setBestaetigen] = useState(null);

  const lade = useCallback(async () => {
    try {
      const d = (await teilnehmerlisteAPI.get()).data;
      setStand(istStand(d) ? d : 'fehler');
    } catch (err) {
      setStand(err?.response?.status === 403 ? 'verboten' : 'fehler');
    }
  }, []);
  useEffect(() => { lade(); }, [lade]);

  if (stand === null || stand === 'verboten') return null;

  const titel = (
    <CardTitle className="text-base flex items-center gap-2">
      <Users className="w-4 h-4 shrink-0" />Teilnehmerliste in der App („Wer kommt noch?“)
    </CardTitle>
  );

  if (stand === 'fehler') {
    return (
      <Card data-testid="teilnehmerliste-karte">
        <CardHeader className="pb-3">{titel}</CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">Die Einstellungen konnten nicht geladen werden.</p>
          <Button size="sm" variant="outline" onClick={lade} data-testid="teilnehmerliste-neu-laden">Erneut laden</Button>
        </CardContent>
      </Card>
    );
  }

  const vornamenAn = stand.vornamen_zeigen === true;
  const ohneDatumAn = stand.ohne_geburtsdatum_zeigen === true;
  const u = istStand(stand.uebersicht) ? stand.uebersicht : {};
  // Der Server liefert die Schalter auch dann, wenn er die Zahlen nicht ermitteln konnte
  // (dann lauter Nullen mit fehler: true). Nullen wären hier eine falsche Auskunft.
  const zahlenFehlen = u.fehler === true;
  const wann = zeitpunkt(stand.geaendert_am);
  const wer = typeof stand.geaendert_von === 'string' ? stand.geaendert_von.trim() : '';

  const speichern = async (werte, erfolg) => {
    setBestaetigen(null);
    setBusy(true);
    try {
      const d = (await teilnehmerlisteAPI.put(werte)).data;
      // Erfolg nur melden, wenn die Antwort den gewünschten Stand auch wirklich zeigt.
      const uebernommen = istStand(d) && Object.entries(werte).every(([k, v]) => (d[k] === true) === v);
      if (istStand(d)) setStand(d); else await lade();
      if (uebernommen) toast.success(erfolg);
      else toast.error('Der Server hat die Änderung nicht bestätigt – bitte den Stand prüfen');
    } catch (err) {
      // Der Server begründet seine Absage selbst — etwa 409, wenn „ohne Geburtsdatum“ bei
      // ausgeschaltetem Hauptschalter eingeschaltet werden soll (z. B. aus einem alten Tab).
      // Genau dieser Text steht im Toast.
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail : 'Nicht gespeichert');
      // Stand neu laden: die Schalter sollen zeigen, was wirklich gilt.
      await lade();
    }
    setBusy(false);
  };

  // „Ohne Geburtsdatum“ wird nie stillschweigend mitgeschaltet: Jedes Umschalten des
  // Hauptschalters — an wie aus — schickt den zweiten Schalter ausdrücklich als „aus“ mit,
  // gleich, was diese Karte gerade anzeigt (ein alter Tab kennt den wahren Stand nicht).
  // So braucht jedes Einschalten seinen eigenen bewussten Klick. Der Server erzwingt
  // dasselbe; hier steht es, damit die Anfrage sagt, was gemeint ist.
  const mitHauptschalter = (an) => ({ vornamen_zeigen: an, ohne_geburtsdatum_zeigen: false });

  const schalteVornamen = (an) => {
    if (an) { setBestaetigen('vornamen_zeigen'); return; }
    speichern(mitHauptschalter(false), 'Vornamen der Teilnehmer ausgeschaltet');
  };
  const schalteOhneDatum = (an) => {
    if (an) { setBestaetigen('ohne_geburtsdatum_zeigen'); return; }
    speichern({ ohne_geburtsdatum_zeigen: false }, 'Mitglieder ohne Geburtsdatum ausgeschaltet');
  };

  const ohneAngabe = !zahlenFehlen && typeof u.ohne_angabe === 'number' && Number.isFinite(u.ohne_angabe) ? u.ohne_angabe : null;

  return (
    <Card data-testid="teilnehmerliste-karte">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          {titel}
          <Badge variant={vornamenAn ? 'default' : 'secondary'} className="text-[10px] shrink-0" data-testid="teilnehmerliste-status">
            {vornamenAn ? 'Eingeschaltet' : 'Ausgeschaltet'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <SchalterZeile
          testid="teilnehmerliste-vornamen"
          titel="Vornamen der Teilnehmer zeigen"
          text="Mitglieder sehen im Kursplan die Vornamen der angemeldeten Mitglieder. Kein Nachname, kein Foto. Nie Minderjährige, nie Kinder- und Jugendkurse, nie Probetrainings. Jedes Mitglied bekommt vorher einen Hinweis in der App und kann seinen Vornamen im Profil ausblenden."
          an={vornamenAn}
          gesperrt={!istInhaber || busy}
          wartet={bestaetigen === 'vornamen_zeigen' && !vornamenAn && istInhaber && !busy}
          frage="Wirklich einschalten? Die App zeigt den Mitgliedern dann den Hinweis; danach steht ihr Vorname in der Teilnehmerliste, solange sie ihn nicht ausblenden."
          onSchalten={schalteVornamen}
          onJa={() => speichern(mitHauptschalter(true), 'Vornamen der Teilnehmer eingeschaltet')}
          onAbbrechen={() => setBestaetigen(null)}
        />
        <SchalterZeile
          testid="teilnehmerliste-ohne-geburtsdatum"
          titel="Auch Mitglieder ohne Geburtsdatum zeigen"
          text="Ohne Geburtsdatum lässt sich die Volljährigkeit nicht belegen. Wer einen Kinder- oder Jugendtarif hat oder als Kind geführt wird, bleibt immer ausgeblendet. Unter den übrigen können Jugendliche mit Erwachsenentarif sein."
          an={ohneDatumAn}
          gesperrt={!istInhaber || busy || !vornamenAn}
          sperrgrund={istInhaber && !vornamenAn ? 'Erst „Vornamen der Teilnehmer zeigen“ einschalten' : ''}
          wartet={bestaetigen === 'ohne_geburtsdatum_zeigen' && vornamenAn && !ohneDatumAn && istInhaber && !busy}
          frage={ohneAngabe === null
            ? 'Wirklich einschalten? Die Volljährigkeit dieser Mitglieder ist nicht belegt.'
            : `Wirklich einschalten? Betrifft ${ohneAngabe} ${ohneAngabe === 1 ? 'Mitglied' : 'Mitglieder'} ohne Geburtsdatum – die Volljährigkeit ist nicht belegt.`}
          onSchalten={schalteOhneDatum}
          onJa={() => speichern({ ohne_geburtsdatum_zeigen: true }, 'Mitglieder ohne Geburtsdatum eingeschaltet')}
          onAbbrechen={() => setBestaetigen(null)}
        />
        {!istInhaber && (
          <p className="text-xs text-muted-foreground" data-testid="teilnehmerliste-nur-inhaber">Nur der Inhaber kann diese Schalter ändern.</p>
        )}
        <div className="rounded-md border border-border/60 p-3 space-y-1.5 text-xs text-muted-foreground" data-testid="teilnehmerliste-uebersicht">
          {zahlenFehlen ? (
            <p>Die Übersicht konnte gerade nicht ermittelt werden. Die Schalter gelten wie angezeigt.</p>
          ) : (
            <>
              <p>
                Mitglieder (laufender Vertrag oder Leitung): <Wert>{zahl(u.mitglieder)}</Wert> · nachweislich volljährig: <Wert>{zahl(u.volljaehrig)}</Wert> · ohne
                Geburtsdatum: <Wert>{zahl(u.ohne_angabe)}</Wert> · minderjährig oder als Kind geführt: <Wert>{zahl(u.minderjaehrig)}</Wert> · ungeklärt: <Wert>{zahl(u.ungeklaert)}</Wert>
              </p>
              <p>
                Kommen in Frage: <Wert>{zahl(u.in_frage)}</Wert> · Hinweis gesehen: <Wert>{zahl(u.hinweis_gesehen)}</Wert> · ausgeblendet: <Wert>{zahl(u.ausgeblendet)}</Wert> · aktuell
                sichtbar: <Wert>{zahl(u.sichtbar)}</Wert>
              </p>
            </>
          )}
        </div>
        {(wann || wer) && (
          <p className="text-xs text-muted-foreground" data-testid="teilnehmerliste-geaendert">
            Zuletzt geändert{wann ? `: ${wann} Uhr` : ''}{wer ? ` von ${wer}` : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
